import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Check, X, Eye, AlertTriangle, Users, Calendar as CalendarIcon, Search, FileText, Paperclip, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { leaveApi, employeeApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Label } from '../ui/label';
import { useAuth } from '../../contexts/AuthContext';
import DocumentViewer from '../ui/document-viewer';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

const TeamApprovalsNew: React.FC = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [filterStatus, setFilterStatus] = useState<string>('PENDING');
  const [filterEmployee, setFilterEmployee] = useState<string>('all');
  const [searchEmployee, setSearchEmployee] = useState<string>('');
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  // Fetch all team leave requests (so summary counts are accurate)
  const { data: allLeaveRequests = [], isLoading } = useQuery({
    queryKey: ['teamLeaveRequests'],
    queryFn: () => leaveApi.requests.getTeamRequests({}),
    refetchOnWindowFocus: true,
  });

  // Fetch employees
  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  // Fetch policies
  const { data: policies = [] } = useQuery({
    queryKey: ['leavePolicies'],
    queryFn: () => leaveApi.policies.getAll(),
  });

  // Fetch all leave requests to detect conflicts
  const { data: allConflictRequests = [] } = useQuery({
    queryKey: ['allLeaveRequests'],
    queryFn: () => leaveApi.requests.getAll({}),
  });

  const getEmployeeName = (request: any) => {
    if (request.employee_name?.trim()) return request.employee_name;
    const emp = employees.find((e: any) => e.id === request.employee_id);
    return emp ? `${emp.first_name || ''} ${emp.last_name || ''}`.trim() : 'Unknown';
  };

  const getEmployeeInitials = (request: any) => {
    const name = getEmployeeName(request);
    const parts = name.split(' ');
    const first = parts[0]?.charAt(0) || '';
    const last = parts[parts.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getEmployeeDepartment = (employeeId: string) => {
    const emp = employees.find((e: any) => e.id === employeeId);
    return emp?.department_id || 'N/A';
  };

  // Filter locally based on status, employee, and search query
  const leaveRequests = useMemo(() => {
    let filtered = allLeaveRequests;
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter((r: any) => r.status === filterStatus);
    }
    
    if (filterEmployee !== 'all') {
      filtered = filtered.filter((r: any) => r.employee_id === filterEmployee);
    }
    
    if (searchEmployee.trim()) {
      const search = searchEmployee.toLowerCase();
      filtered = filtered.filter((request: any) => 
        getEmployeeName(request).toLowerCase().includes(search)
      );
    }
    
    return filtered;
  }, [allLeaveRequests, filterStatus, filterEmployee, searchEmployee, employees]);

  // Unique employees for filter
  const uniqueEmployees = useMemo(() => {
    const empMap = new Map();
    allLeaveRequests.forEach((req: any) => {
      if (!empMap.has(req.employee_id)) {
        empMap.set(req.employee_id, getEmployeeName(req));
      }
    });
    return Array.from(empMap.entries()).map(([id, name]) => ({ id, name }));
  }, [allLeaveRequests]);

  // Conflict logic
  const getLeaveConflicts = (request: any) => {
    if (!request) return [];
    const start = new Date(request.start_date);
    const end = new Date(request.end_date);
    return allConflictRequests.filter((lr: any) => {
      if (lr.id === request.id || (lr.status !== 'APPROVED' && lr.status !== 'PENDING')) return false;
      const lrStart = new Date(lr.start_date);
      const lrEnd = new Date(lr.end_date);
      return (lrStart <= end && lrEnd >= start);
    });
  };

  const getDepartmentAbsencePercentage = (request: any) => {
    if (!request) return 0;
    const requestEmp = employees.find((e: any) => e.id === request.employee_id);
    if (!requestEmp?.department_id) return 0;
    const deptEmps = employees.filter((e: any) => e.department_id === requestEmp.department_id);
    if (deptEmps.length === 0) return 0;
    const conflicts = getLeaveConflicts(request);
    const deptConflicts = conflicts.filter((c: any) => 
      employees.find((e: any) => e.id === c.employee_id)?.department_id === requestEmp.department_id
    );
    return Math.round(((deptConflicts.length + 1) / deptEmps.length) * 100);
  };

  // Calculate summary stats
  const pendingCount = allLeaveRequests.filter((r: any) => r.status === 'PENDING').length;
  const approvedCount = allLeaveRequests.filter((r: any) => r.status === 'APPROVED').length;
  const rejectedCount = allLeaveRequests.filter((r: any) => r.status === 'REJECTED').length;

  const approveMutation = useMutation({
    mutationFn: (requestId: string) => leaveApi.requests.approve(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['allLeaveRequests'] });
      toast({ title: 'Leave request approved' });
      setIsViewOpen(false);
    },
    onError: (error: Error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  });

  const rejectMutation = useMutation({
    mutationFn: (requestId: string) => leaveApi.requests.reject(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teamLeaveRequests'] });
      queryClient.invalidateQueries({ queryKey: ['allLeaveRequests'] });
      toast({ title: 'Leave request rejected' });
      setIsViewOpen(false);
    },
    onError: (error: Error) => toast({ title: 'Error', description: error.message, variant: 'destructive' }),
  });

  const getStatusBadge = (status: string) => {
    const statusColors = {
      PENDING: 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400',
      APPROVED: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      CANCELLED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
    };
    return (
      <Badge className={cn("px-3 py-1 rounded-full font-bold text-[11px] tracking-wide", statusColors[status as keyof typeof statusColors] || '')} variant="outline">
        {status}
      </Badge>
    );
  };

  const handleView = (request: any) => {
    setSelectedRequest(request);
    setIsViewOpen(true);
    setReviewNotes('');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading approvals...</span>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-500">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-[#143A62] dark:text-slate-100">Review and manage leave requests from your team</h2>
         
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: 'Pending Approval', count: pendingCount, icon: Clock, color: 'amber' },
          { label: 'Approved', count: approvedCount, icon: CheckCircle2, color: 'green' },
          { label: 'Rejected', count: rejectedCount, icon: XCircle, color: 'red' }
        ].map((stat, i) => (
          <Card key={i} className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="space-y-4">
                  <p className="text-sm font-bold text-slate-400 tracking-wide">{stat.label}</p>
                  <div className="space-y-1">
                    <h3 className={cn("text-4xl font-bold mt-1", 
                      stat.color === 'amber' ? 'text-amber-600' : 
                      stat.color === 'green' ? 'text-green-600' : 'text-red-600'
                    )}>{stat.count}</h3>
                    
                  </div>
                </div>
                <div className={cn("p-3 rounded-2xl", 
                  stat.color === 'amber' ? 'bg-amber-50 text-amber-500' : 
                  stat.color === 'green' ? 'bg-green-50 text-green-500' : 'bg-red-50 text-red-500'
                )}>
                  <stat.icon className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 items-end bg-white/50 dark:bg-slate-900/50 p-6 rounded-2xl border border-slate-100 dark:border-slate-800 backdrop-blur-sm">
        <div className="w-full md:w-48 space-y-1.5">
          <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold ml-1">Filter by Status</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="h-12 rounded-xl bg-white dark:bg-slate-900 border-slate-200 font-bold text-sm shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-slate-100 shadow-xl">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="PENDING">Pending</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="w-full md:w-64 space-y-1.5">
          <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold ml-1">Filter by Employee</Label>
          <Select value={filterEmployee} onValueChange={setFilterEmployee}>
            <SelectTrigger className="h-12 rounded-xl bg-white dark:bg-slate-900 border-slate-200 font-bold text-sm shadow-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-slate-100 shadow-xl">
              <SelectItem value="all">All Employees</SelectItem>
              {uniqueEmployees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex-1 space-y-1.5">
          <Label className="text-slate-700 dark:text-slate-300 text-sm font-semibold ml-1">Search Employee</Label>
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <Input
              placeholder="Type employee name..."
              value={searchEmployee}
              onChange={(e) => setSearchEmployee(e.target.value)}
              className="pl-11 h-12 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 rounded-xl shadow-sm font-bold text-sm"
            />
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2 ml-1">
          <span className="text-blue-500 font-black">⚡</span>
          Leave Requests
        </h2>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-14 px-8">Employee</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-14">Leave Type</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-14">Duration</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-14 text-center">Days</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-14">Status</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-14 text-center">Docs</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-14">Conflicts</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-14 text-right px-8">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaveRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-24">
                      <div className="flex flex-col items-center">
                        <Users className="h-14 w-14 text-slate-100 dark:text-slate-800 mb-4" />
                        <h3 className="text-lg font-bold text-slate-400">No requests found</h3>
                        <p className="text-sm text-slate-400 mt-1 font-medium">All caught up! No leave requests to review.</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  leaveRequests.map((request: any) => {
                    const policy = policies.find((p: any) => p.id === request.leave_policy_id);
                    const conflicts = getLeaveConflicts(request);
                    const absencePct = getDepartmentAbsencePercentage(request);
                    return (
                      <TableRow key={request.id} className="group border-b border-slate-50 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 transition-colors">
                        <TableCell className="px-8 py-5">
                          <div className="flex items-center space-x-4">
                            <Avatar className="h-10 w-10 rounded-xl shadow-sm border border-slate-100">
                              <AvatarImage src={request.avatar_url} />
                              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-[#143A62] text-white font-bold text-xs">
                                {getEmployeeInitials(request)}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-bold text-[#143A62] dark:text-slate-100 text-[15px]">{getEmployeeName(request)}</p>
                              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Dept: {getEmployeeDepartment(request.employee_id)}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-bold text-slate-600 dark:text-slate-300">{policy?.leave_type || 'General'}</span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-slate-600">
                              {format(new Date(request.start_date), 'MMM dd')} - {format(new Date(request.end_date), 'MMM dd')}
                            </span>
                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{format(new Date(request.start_date), 'yyyy')}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-sm font-black text-[#143A62] dark:text-white bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-lg">
                            {request.days_count}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                        <TableCell className="text-center">
                          {request.document_url ? (
                            <Badge variant="outline" className="text-[10px] font-bold border-blue-200 text-blue-600 bg-blue-50/50 px-2">
                              <Paperclip className="h-3 w-3 mr-1" />
                              1 Doc
                            </Badge>
                          ) : <span className="text-slate-300">—</span>}
                        </TableCell>
                        <TableCell>
                          {conflicts.length > 0 ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive" className="px-2 py-0.5 h-6 text-[10px] font-black uppercase">
                                <Users className="h-3 w-3 mr-1" /> {conflicts.length} Conflict{conflicts.length > 1 ? 's' : ''}
                              </Badge>
                              {absencePct >= 50 && (
                                <Badge variant="outline" className="px-2 py-0.5 h-6 text-[10px] font-bold border-orange-200 text-orange-600 bg-orange-50">
                                  {absencePct}% ABSENT
                                </Badge>
                              )}
                            </div>
                          ) : <span className="text-[11px] font-bold text-slate-400 uppercase tracking-tight">No conflicts</span>}
                        </TableCell>
                        <TableCell className="text-right px-8">
                          <Button 
                            onClick={() => handleView(request)}
                            variant="outline" 
                            size="sm"
                          >
                            <Eye className="h-3.5 w-3.5 mr-2" />
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Review Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-0 border-none overflow-hidden">
          <DialogHeader className="px-6 py-5 border-b border-slate-50 dark:border-slate-800">
            <div className="flex items-center space-x-4">
              <Avatar className="h-14 w-14 rounded-2xl shadow-sm border border-slate-100">
                <AvatarFallback className="bg-[#143A62] text-white font-bold text-lg">
                  {selectedRequest && getEmployeeInitials(selectedRequest)}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col">
                <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
                  {selectedRequest && getEmployeeName(selectedRequest)}
                </DialogTitle>
                <div className="flex items-center space-x-2 mt-0.5">
                  <p className="text-xs text-blue-500 font-bold uppercase tracking-widest">
                    {selectedRequest && getEmployeeDepartment(selectedRequest)}
                  </p>
                  <Badge className="bg-emerald-100 text-emerald-600 hover:bg-emerald-200 rounded-md px-1.5 py-0 text-[10px] font-black">
                    {selectedRequest?.status}
                  </Badge>
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="px-6 py-4 space-y-4">
            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800">
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-400 tracking-widest">Leave Type</Label>
                <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                  {selectedRequest && policies.find((p: any) => p.id === selectedRequest.leave_policy_id)?.leave_type || 'ANNUAL'}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-bold text-slate-400 tracking-widest">Duration</Label>
                <p className="font-bold text-slate-800 dark:text-slate-200 text-sm">
                  {selectedRequest?.days_count} Days
                  <span className="text-slate-400 font-medium text-xs ml-1">
                    ({selectedRequest && format(new Date(selectedRequest.start_date), 'MMM d')} - {selectedRequest && format(new Date(selectedRequest.end_date), 'MMM d')})
                  </span>
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold text-slate-400 tracking-widest ml-1">Employee Reason</Label>
              <div className="p-3 bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-800 text-slate-600 dark:text-slate-300 font-medium italic text-sm shadow-sm">
                {selectedRequest?.reason || 'No reason provided.'}
              </div>
            </div>

            {selectedRequest?.document_url && (
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="h-10 w-10 bg-white dark:bg-slate-900 rounded-xl flex items-center justify-center text-slate-500 shadow-sm border border-slate-200 dark:border-slate-700">
                    <Paperclip className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200">Attachment Provided</p>
                  </div>
                </div>
                <DocumentViewer
                  documentUrl={selectedRequest.document_url}
                  documentFilename={selectedRequest.document_filename || 'Leave Document'}
                />
              </div>
            )}

            {/* Conflict Detection Logic inside Modal */}
            {selectedRequest && (() => {
               const conflicts = getLeaveConflicts(selectedRequest);
               if (conflicts.length > 0) {
                 return (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-2xl border border-red-100 flex items-center space-x-3">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="text-sm font-bold text-red-700 dark:text-red-300">Conflict: {conflicts.length} team members already away</p>
                        <p className="text-[11px] font-semibold text-red-600/80 mt-0.5">Overlap: {conflicts.map(c => getEmployeeName(c)).join(', ')}</p>
                      </div>
                    </div>
                 )
               }
               return (
                  <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl border border-emerald-100 flex items-center space-x-3">
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">No conflicts detected for these dates.</p>
                  </div>
               );
            })()}

            {selectedRequest?.status === 'PENDING' && (
              <div className="space-y-1.5 pt-1">
                <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Approval/Rejection Notes</Label>
                <Textarea
                  placeholder="Add your comments here..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  className="rounded-2xl border-slate-200 focus:ring-blue-500/10 min-h-[60px] font-medium text-sm shadow-sm bg-white resize-none"
                />
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 flex gap-2 border-t border-slate-50 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
            <Button variant="ghost" onClick={() => setIsViewOpen(false)}>Cancel</Button>
            {selectedRequest?.status === 'PENDING' && (
              <>
                <Button 
                  onClick={() => rejectMutation.mutate(selectedRequest.id)}
                  variant="destructive"
                  className="ml-auto"
                >
                  <X className="h-4 w-4 mr-1.5" />
                  Reject
                </Button>
                <Button 
                  onClick={() => approveMutation.mutate(selectedRequest.id)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white dark:bg-emerald-650 dark:hover:bg-emerald-700"
                >
                  <Check className="h-4 w-4 mr-1.5" />
                  Approve
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamApprovalsNew;
